/**
 * CompanyInternalFeed — Internal company feed for the Lending Network
 * Route: /app/company
 *
 * Auto-detects the logged-in user's company and shows:
 * - Posts tab   : internal company posts (text, image, video)
 * - People tab  : all employees from this company
 * - About tab   : company details
 * - Page Admin  : manage managers (super_admin only)
 *
 * Only company members can see this feed (internal, not public).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button }       from 'primereact/button';
import { Avatar }       from 'primereact/avatar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog }       from 'primereact/dialog';
import { InputText }    from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown }     from 'primereact/dropdown';
import { Skeleton }     from 'primereact/skeleton';
import { Toast }        from 'primereact/toast';
import { Tag }          from 'primereact/tag';
import { FileUpload }   from 'primereact/fileupload';
import { Menu }          from 'primereact/menu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import companyPageService from '../../services/companyPageService';
import socialFeedService  from '../../services/socialFeedService';
import ConnectionService  from '../../services/agentProfile/ConnectionService';

// ── Helpers ───────────────────────────────────────────────────────────────────
const initials = (name) =>
    name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

const fmtNum = (n) => {
    if (!n && n !== 0) return '0';
    if (n >= 10000000) return `${(n / 10000000).toFixed(1)} Cr`;
    if (n >= 100000)   return `${(n / 100000).toFixed(1)} L`;
    if (n >= 1000)     return `${(n / 1000).toFixed(1)}K`;
    return String(n);
};

const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
    if (d === 0) return 'Today';
    if (d === 1) return 'Yesterday';
    if (d < 7)  return `${d}d ago`;
    if (d < 30) return `${Math.floor(d / 7)}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
};

// ── Role definitions ──────────────────────────────────────────────────────────
const ROLES = [
    { label: '⭐ Super Admin', value: 'super_admin', bg: '#FFF7ED', color: '#C2410C' },
    { label: '✏️ Editor',      value: 'editor',      bg: '#EFF6FF', color: '#1D4ED8' },
];
const roleInfo = (role) =>
    ROLES.find(r => r.value === role) || { label: 'Member', bg: '#F8FAFC', color: '#64748B' };

// ── Post type options ─────────────────────────────────────────────────────────
const POST_TYPES = [
    { label: 'Update',      value: 'general'     },
    { label: 'Achievement', value: 'achievement' },
    { label: 'Article',     value: 'article'     },
];

// ─────────────────────────────────────────────────────────────────────────────
const CompanyInternalFeed = () => {
    const navigate = useNavigate();
    const toast    = useRef(null);
    const { slug: slugParam } = useParams(); // present when viewing /app/company-pages/:slug

    // ── Page state ────────────────────────────────────────────────────────────
    const [loading,   setLoading]   = useState(true);
    const [pageData,  setPageData]  = useState(null);
    const [slug,      setSlug]      = useState(slugParam || null);

    const canPost     = pageData?.can_edit      || false;
    const isSuperAdmin = pageData?.is_super_admin || false;
    const myRole      = pageData?.my_role       || null;

    // ── Tab ───────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);

    // ── Posts ─────────────────────────────────────────────────────────────────
    const [posts,          setPosts]          = useState([]);
    const [postsLoading,   setPostsLoading]   = useState(false);
    const [postsPage,      setPostsPage]      = useState(1);
    const [postsLastPage,  setPostsLastPage]  = useState(1);

    // ── People ────────────────────────────────────────────────────────────────
    const [people,           setPeople]           = useState([]);
    const [peopleLoading,    setPeopleLoading]    = useState(false);
    const [peopleSearch,     setPeopleSearch]     = useState('');
    // connectingId: profile id currently being connected to; sentIds: set of sent profile ids
    const [connectingId,     setConnectingId]     = useState(null);
    const [sentConnectIds,   setSentConnectIds]   = useState(new Set());

    // ── Managers ──────────────────────────────────────────────────────────────
    const [managers,        setManagers]        = useState([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [removingId,      setRemovingId]      = useState(null);

    // ── Post creation dialog ──────────────────────────────────────────────────
    const [postDialog,        setPostDialog]        = useState(false);
    const [postContent,       setPostContent]       = useState('');
    const [postType,          setPostType]          = useState('general');
    const [postMediaType,     setPostMediaType]     = useState(null); // null | 'image' | 'video'
    const [postVideoUrl,      setPostVideoUrl]      = useState('');   // video link only
    const [uploadedImageUrls, setUploadedImageUrls] = useState([]);   // S3 URLs for uploaded images
    const [uploadingMedia,    setUploadingMedia]    = useState(false);
    const [postTags,          setPostTags]          = useState('');
    const [postSubmitting,    setPostSubmitting]    = useState(false);
    const fileUploadRef = useRef(null);

    // ── Post interactions (3-dots, comment, save) ────────────────────────────
    const postMenuRef = useRef(null);
    const [menuTargetPost,    setMenuTargetPost]    = useState(null);
    const [openCommentPostId, setOpenCommentPostId] = useState(null);
    const [commentText,       setCommentText]       = useState('');
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [postComments,      setPostComments]      = useState({}); // { [postId]: comments[] }
    const [commentsLoading,   setCommentsLoading]   = useState(null); // postId currently loading
    const [savingId,          setSavingId]          = useState(null);

    // ── Give Access dialog ────────────────────────────────────────────────────
    const [giveAccessDialog,  setGiveAccessDialog]  = useState(false);
    const [employees,         setEmployees]         = useState([]);
    const [employeesLoading,  setEmployeesLoading]  = useState(false);
    const [employeeSearch,    setEmployeeSearch]    = useState('');
    const [employeeRoles,     setEmployeeRoles]     = useState({});

    // ── Rating ────────────────────────────────────────────────────────────────
    const [myRating,    setMyRating]    = useState(null);
    const [ratingBusy,  setRatingBusy]  = useState(false);
    const [grantingId,        setGrantingId]        = useState(null);

    // ── Fetch my company page ─────────────────────────────────────────────────
    const fetchMyCompany = useCallback(async () => {
        setLoading(true);
        try {
            // If a slug was passed via URL param, load that company; else load user's own
            const res = slugParam
                ? await companyPageService.getPage(slugParam)
                : await companyPageService.getMyCompanyPage();
            if (res.success && res.data) {
                setPageData(res.data);
                setSlug(res.data.slug);
                if (res.data.my_rating) setMyRating(res.data.my_rating);
            } else if (!res.success) {
                toast.current?.show({ severity: 'error', summary: 'Load Error', detail: res.message || 'Failed to load company page', life: 4000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Network Error', detail: 'Could not load company page. Check your connection.', life: 4000 });
        } finally {
            setLoading(false);
        }
    }, [slugParam]);

    useEffect(() => { fetchMyCompany(); }, [fetchMyCompany]);

    // ── Fetch posts ───────────────────────────────────────────────────────────
    const fetchPosts = useCallback(async (page = 1) => {
        if (!slug) return;
        setPostsLoading(true);
        try {
            const res = await companyPageService.getPagePosts(slug, { perPage: 10, page });
            if (res.success) {
                const items = res.data.data || [];
                setPosts(page === 1 ? items : prev => [...prev, ...items]);
                setPostsPage(res.data.current_page || 1);
                setPostsLastPage(res.data.last_page || 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Posts Error', detail: res.message || 'Failed to load posts', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Posts Error', detail: 'Could not load posts. Check your connection.', life: 3000 });
        } finally {
            setPostsLoading(false);
        }
    }, [slug]);

    useEffect(() => { if (slug) fetchPosts(1); }, [slug, fetchPosts]);

    // ── Fetch people ──────────────────────────────────────────────────────────
    const fetchPeople = useCallback(async () => {
        if (!slug) return;
        setPeopleLoading(true);
        try {
            const res = await companyPageService.getPagePeople(slug, { perPage: 100, page: 1 });
            if (res.success) {
                setPeople(res.data.data || []);
            } else {
                toast.current?.show({ severity: 'error', summary: 'People Error', detail: res.message || 'Failed to load people', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'People Error', detail: 'Could not load people. Check your connection.', life: 3000 });
        } finally {
            setPeopleLoading(false);
        }
    }, [slug]);

    // ── Fetch managers ────────────────────────────────────────────────────────
    const fetchManagers = useCallback(async () => {
        if (!slug) return;
        setManagersLoading(true);
        try {
            const res = await companyPageService.getManagers(slug);
            if (res.success) {
                setManagers(res.data || []);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Managers Error', detail: res.message || 'Failed to load managers', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Managers Error', detail: 'Could not load managers. Check your connection.', life: 3000 });
        } finally {
            setManagersLoading(false);
        }
    }, [slug]);

    // ── Tab change → lazy-load data ───────────────────────────────────────────
    const handleTabChange = (e) => {
        setActiveTab(e.index);
        if (e.index === 1 && people.length === 0)   fetchPeople();
        if (e.index === 3 && managers.length === 0) fetchManagers();
    };

    // ── Post submission ───────────────────────────────────────────────────────
    const resetPostForm = () => {
        setPostContent('');
        setPostType('general');
        setPostMediaType(null);
        setPostVideoUrl('');
        setUploadedImageUrls([]);
        setUploadingMedia(false);
        setPostTags('');
        fileUploadRef.current?.clear();
    };

    // ── S3 file upload handler ────────────────────────────────────────────────
    const handleFileUpload = async (event) => {
        const files = event.files;
        if (!files || files.length === 0) return;
        setUploadingMedia(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files[]', file));
            const res = await socialFeedService.uploadMedia(formData);
            if (res.success && res.data?.length > 0) {
                const urls = res.data.map(m => m.url).filter(Boolean);
                setUploadedImageUrls(urls);
                setPostMediaType('image');
                toast.current?.show({ severity: 'success', summary: 'Uploaded', detail: `${urls.length} image${urls.length > 1 ? 's' : ''} ready to post`, life: 2000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Upload failed', detail: res.message || 'Could not upload image', life: 3000 });
                fileUploadRef.current?.clear();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Upload error', detail: 'Something went wrong uploading your image', life: 3000 });
            fileUploadRef.current?.clear();
        } finally {
            setUploadingMedia(false);
        }
    };

    const handlePostSubmit = async () => {
        if (!postContent.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Post content cannot be empty', life: 3000 });
            return;
        }
        setPostSubmitting(true);
        try {
            const mediaPayload = (() => {
                if (postMediaType === 'image' && uploadedImageUrls.length > 0) {
                    return { media_type: 'image', media_urls: uploadedImageUrls };
                }
                if (postMediaType === 'video' && postVideoUrl.trim()) {
                    return { media_type: 'video', media_urls: [postVideoUrl.trim()] };
                }
                return {};
            })();
            const payload = {
                content:   postContent,
                post_type: postType,
                tags:      postTags.split(',').map(t => t.trim()).filter(Boolean),
                ...mediaPayload,
            };
            const res = await companyPageService.createOfficialPost(slug, payload);
            if (res.success) {
                resetPostForm();
                setPostDialog(false);
                fetchPosts(1);
                toast.current?.show({ severity: 'success', summary: 'Posted!', detail: `Your post is live on ${pageData.name}'s feed`, life: 3000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to post', life: 3000 });
            }
        } finally {
            setPostSubmitting(false);
        }
    };

    // ── Like (local toggle for demo) ──────────────────────────────────────────
    const handleLike = (post) => {
        setPosts(prev => prev.map(p =>
            p.id === post.id
                ? { ...p, is_liked: !p.is_liked, like_count: (p.like_count || 0) + (p.is_liked ? -1 : 1) }
                : p
        ));
    };

    // ── Post 3-dots menu ──────────────────────────────────────────────────────
    const handlePostMenu = (e, post) => {
        setMenuTargetPost(post);
        postMenuRef.current?.toggle(e);
    };

    const getPostMenuItems = () => {
        if (!menuTargetPost) return [];
        const isSaved = menuTargetPost.is_saved;
        const items = [
            {
                label: isSaved ? 'Unsave Post' : 'Save Post',
                icon: isSaved ? 'pi pi-bookmark-fill' : 'pi pi-bookmark',
                command: () => handleSaveToggle(menuTargetPost),
            },
        ];
        if (canPost) {
            items.push({ separator: true });
            items.push({
                label: 'Delete Post',
                icon: 'pi pi-trash',
                command: () => handleDeletePost(menuTargetPost),
            });
        }
        return items;
    };

    const handleDeletePost = (post) => {
        confirmDialog({
            message: 'Are you sure you want to delete this post?',
            header: 'Delete Post',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const res = await socialFeedService.deletePost(post.id);
                    if (res.success) {
                        setPosts(prev => prev.filter(p => p.id !== post.id));
                        toast.current?.show({ severity: 'info', summary: 'Deleted', detail: 'Post deleted', life: 2000 });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to delete post', life: 3000 });
                    }
                } catch {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete post', life: 3000 });
                }
            },
        });
    };

    // ── Save / Bookmark ───────────────────────────────────────────────────────
    const handleSaveToggle = async (post) => {
        if (savingId) return;
        setSavingId(post.id);
        const newSaved = !post.is_saved;
        try {
            const res = await socialFeedService.toggleSave(post.id, newSaved);
            if (res.success) {
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, is_saved: newSaved } : p));
                toast.current?.show({ severity: 'success', summary: newSaved ? 'Saved' : 'Removed', detail: newSaved ? 'Post saved to your collection' : 'Post removed from saved', life: 2000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to save post', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save post', life: 3000 });
        } finally {
            setSavingId(null);
        }
    };

    // ── Comments ──────────────────────────────────────────────────────────────
    const handleCommentToggle = async (postId) => {
        const closing = openCommentPostId === postId;
        setOpenCommentPostId(closing ? null : postId);
        setCommentText('');
        if (!closing && !postComments[postId]) {
            setCommentsLoading(postId);
            try {
                const res = await socialFeedService.getComments(postId);
                if (res.success) {
                    setPostComments(prev => ({ ...prev, [postId]: res.data?.data || res.data || [] }));
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to load comments', life: 3000 });
                }
            } catch {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load comments', life: 3000 });
            } finally { setCommentsLoading(null); }
        }
    };

    const handleCommentSubmit = async (post) => {
        if (!commentText.trim()) return;
        setCommentSubmitting(true);
        try {
            const res = await socialFeedService.addComment(post.id, commentText.trim());
            if (res.success) {
                // Normalise to getComments shape: { id, comment_text, user_profile: { full_name } }
                const raw = res.data || {};
                const newComment = {
                    id: raw.id || Date.now(),
                    comment_text: raw.comment_text || commentText.trim(),
                    created_at: raw.created_at || new Date().toISOString(),
                    user_profile: {
                        full_name: raw.profile?.full_name || 'You',
                        profile_photo_url: raw.profile?.profile_photo_url || null,
                    },
                };
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
                setPostComments(prev => ({ ...prev, [post.id]: [newComment, ...(prev[post.id] || [])] }));
                setCommentText('');
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to add comment', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to add comment', life: 3000 });
        } finally {
            setCommentSubmitting(false);
        }
    };


    // ── Give Access dialog ────────────────────────────────────────────────────
    const openGiveAccessDialog = async () => {
        setGiveAccessDialog(true);
        setEmployeeSearch('');
        setEmployeesLoading(true);
        try {
            const res = await companyPageService.getPagePeople(slug, { perPage: 100, page: 1 });
            if (res.success) {
                const eligible = (res.data.data || []).filter(e => !e.page_role || e.page_role === 'member');
                setEmployees(eligible);
                const defaults = {};
                eligible.forEach(e => { defaults[e.id] = 'editor'; });
                setEmployeeRoles(defaults);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to load employees', life: 3000 });
                setGiveAccessDialog(false);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load employees. Check your connection.', life: 3000 });
            setGiveAccessDialog(false);
        } finally {
            setEmployeesLoading(false);
        }
    };

    const handleGrantAccess = async (emp) => {
        if (grantingId) return;
        setGrantingId(emp.id);
        const role = employeeRoles[emp.id] || 'editor';
        try {
            const res = await companyPageService.addManager(slug, emp.user_id, role);
            if (res.success) {
                setEmployees(prev => prev.filter(e => e.id !== emp.id));
                fetchManagers();
                fetchPeople();
                const ri = roleInfo(role);
                toast.current?.show({ severity: 'success', summary: 'Access Granted', detail: `${emp.full_name} added as ${ri.label.replace(/\W/g, ' ').trim()}`, life: 3000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to grant access', life: 3000 });
            }
        } finally {
            setGrantingId(null);
        }
    };

    const handleRemoveManager = async (managerId, name) => {
        if (removingId) return;
        setRemovingId(managerId);
        try {
            const res = await companyPageService.removeManager(slug, managerId);
            if (res.success) {
                setManagers(prev => prev.filter(m => m.id !== managerId));
                fetchPeople();
                toast.current?.show({ severity: 'info', summary: 'Access Removed', detail: `${name}'s page access removed`, life: 3000 });
            }
        } finally {
            setRemovingId(null);
        }
    };

    const handleRoleChange = async (managerId, newRole) => {
        try {
            const res = await companyPageService.updateManagerRole(slug, managerId, newRole);
            if (res.success) {
                setManagers(prev => prev.map(m => m.id === managerId ? { ...m, role: newRole } : m));
                toast.current?.show({ severity: 'success', summary: 'Role Updated', detail: 'Manager role updated', life: 2000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to update role', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update role', life: 3000 });
        }
    };

    // ── Loading skeleton ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="grid">
                <div className="col-12 lg:col-8">
                    <div className="card p-0 overflow-hidden">
                        <Skeleton height="160px" className="border-round-none" />
                        <div className="p-4">
                            <Skeleton shape="square" size="72px" className="mb-3 border-round" style={{ borderRadius: '10px' }} />
                            <Skeleton height="1.5rem" width="40%" className="mb-2" />
                            <Skeleton height="0.875rem" width="60%" className="mb-3" />
                            <div className="flex gap-4">
                                {[1, 2, 3].map(i => <Skeleton key={i} height="2rem" width="80px" />)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 lg:col-4">
                    <div className="card p-3 mb-3">
                        <Skeleton height="1rem" width="60%" className="mb-3" />
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} height="0.875rem" className="mb-2" />)}
                    </div>
                    <div className="card p-3">
                        <Skeleton height="1rem" width="50%" className="mb-3" />
                        {[1, 2, 3].map(i => <Skeleton key={i} height="0.875rem" className="mb-2" />)}
                    </div>
                </div>
            </div>
        );
    }

    // ── No company page ───────────────────────────────────────────────────────
    if (!pageData) {
        return (
            <div className="grid">
                <div className="col-12">
                    <div className="card text-center" style={{ padding: '64px 24px' }}>
                        <i className="pi pi-building" style={{ fontSize: '3.5rem', color: 'var(--surface-300)', display: 'block', marginBottom: '20px' }} />
                        <h3 className="text-900 font-bold mb-2" style={{ fontSize: '1.1rem' }}>No Company Page Found</h3>
                        <p className="text-color-secondary text-sm mb-4 line-height-3">
                            Your company doesn't have a page on the Lending Network yet.<br />
                            Ask your admin to set up a company page and assign you access.
                        </p>
                        <Button label="Go to Lending Feed" icon="pi pi-arrow-left" className="p-button-outlined" onClick={() => navigate('/app/leadflow/feed')} />
                    </div>
                </div>
            </div>
        );
    }

    // include self (shown with "You" badge); self always first, then others
    const visiblePeople = [...people].sort((a, b) => (b.is_me ? 1 : 0) - (a.is_me ? 1 : 0));

    const handleConnect = async (person) => {
        if (connectingId || sentConnectIds.has(person.id)) return;
        setConnectingId(person.id);
        try {
            const res = await ConnectionService.sendRequest({ receiver_profile_id: person.id });
            if (res.success) {
                setSentConnectIds(prev => new Set(prev).add(person.id));
                toast.current?.show({ severity: 'success', summary: 'Request Sent', detail: `Connection request sent to ${person.full_name}`, life: 3000 });
            } else {
                toast.current?.show({ severity: 'warn', summary: 'Not Sent', detail: res.message || 'Could not send request', life: 3000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to send connection request', life: 3000 });
        } finally {
            setConnectingId(null);
        }
    };

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <>
            <Toast ref={toast} />
            <Menu ref={postMenuRef} model={getPostMenuItems()} popup />
            <ConfirmDialog />


            <div className="grid">

                {/* ═══════════════ LEFT COLUMN ═══════════════ */}
                <div className="col-12 lg:col-8">

                    {/* ── Company Profile Card ── */}
                    <div className="card p-0 mb-3" style={{ borderRadius: '12px', overflow: 'visible' }}>

                        {/* Banner — logo is absolutely positioned inside it, overflowing below */}
                        <div style={{
                            height: '180px',
                            background: 'linear-gradient(135deg, #0f2958 0%, #1e56b0 45%, #4f46e5 80%, #7c3aed 100%)',
                            position: 'relative',
                            borderRadius: '12px 12px 0 0',
                            overflow: 'visible',
                        }}>
                            {/* Geometric overlay (clipped to banner only) */}
                            <div style={{ position: 'absolute', inset: 0, borderRadius: '12px 12px 0 0', overflow: 'hidden', pointerEvents: 'none' }}>
                                <svg style={{ width: '100%', height: '100%', opacity: 0.07 }} preserveAspectRatio="none">
                                    <defs>
                                        <pattern id="cp" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                                            <circle cx="30" cy="30" r="20" fill="none" stroke="#fff" strokeWidth="1" />
                                            <circle cx="0"  cy="0"  r="10" fill="none" stroke="#fff" strokeWidth="0.5" />
                                            <circle cx="60" cy="60" r="10" fill="none" stroke="#fff" strokeWidth="0.5" />
                                        </pattern>
                                    </defs>
                                    <rect width="100%" height="100%" fill="url(#cp)" />
                                </svg>
                            </div>

                            {/* Internal badge — top right */}
                            <div style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 2 }}>
                                <span className="flex align-items-center gap-1 px-3 py-1 border-round-3xl text-white text-xs font-semibold"
                                    style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', letterSpacing: '0.03em' }}>
                                    <i className="pi pi-lock" style={{ fontSize: '0.6rem' }} />
                                    Internal Feed
                                </span>
                            </div>

                            {/* New Post button — bottom right (editors) */}
                            {canPost && (
                                <div style={{ position: 'absolute', bottom: '14px', right: '14px', zIndex: 2 }}>
                                    <Button
                                        label="New Post"
                                        icon="pi pi-plus"
                                        className="p-button-sm"
                                        style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', backdropFilter: 'blur(8px)', fontWeight: '600' }}
                                        onClick={() => setPostDialog(true)}
                                    />
                                </div>
                            )}

                            {/* Logo — anchored to bottom-left of banner, overflows below */}
                            <div style={{
                                position: 'absolute',
                                bottom: '-40px',
                                left: '24px',
                                width: '80px', height: '80px', borderRadius: '12px',
                                background: 'var(--surface-card)', border: '3px solid var(--surface-card)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.75rem', fontWeight: '800', color: '#B31B1B',
                                letterSpacing: '-1px', zIndex: 3,
                            }}>
                                {pageData.name?.slice(0, 2).toUpperCase()}
                            </div>
                        </div>

                        {/* Logo + Info row */}
                        <div className="px-4 pb-4" style={{ paddingTop: '52px' }}>
                            <div className="flex align-items-center justify-content-end mb-2">
                                {/* Role badge aligned right */}
                                {myRole && (
                                    <Tag
                                        value={roleInfo(myRole).label}
                                        style={{ background: roleInfo(myRole).bg, color: roleInfo(myRole).color, border: `1px solid ${roleInfo(myRole).color}30`, fontWeight: '700', fontSize: '0.72rem', padding: '4px 12px', borderRadius: '20px' }}
                                    />
                                )}
                            </div>

                            {/* Name + Badges */}
                            <div className="flex align-items-center flex-wrap gap-2 mb-1">
                                <h1 className="text-900 font-bold m-0" style={{ fontSize: '1.35rem', lineHeight: 1.2 }}>{pageData.name}</h1>
                                <span className="flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold"
                                    style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                                    <i className="pi pi-verified" style={{ fontSize: '0.65rem' }} /> Verified
                                </span>
                                <span className="flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold"
                                    style={{ background: '#F0FDF4', color: '#15803D' }}>
                                    <i className="pi pi-building" style={{ fontSize: '0.65rem' }} /> Lending Network
                                </span>
                            </div>

                            {pageData.tagline && (
                                <p className="text-color-secondary text-sm mt-1 mb-2 line-height-3" style={{ margin: '4px 0 8px' }}>{pageData.tagline}</p>
                            )}

                            {/* Meta row */}
                            <div className="flex flex-wrap gap-3 mb-3">
                                {pageData.industry && (
                                    <span className="flex align-items-center gap-1 text-xs text-color-secondary">
                                        <i className="pi pi-briefcase" style={{ color: 'var(--primary-color)', fontSize: '0.75rem' }} />
                                        {pageData.industry}
                                    </span>
                                )}
                                {pageData.hq_city && (
                                    <span className="flex align-items-center gap-1 text-xs text-color-secondary">
                                        <i className="pi pi-map-marker" style={{ color: 'var(--primary-color)', fontSize: '0.75rem' }} />
                                        {pageData.hq_city}{pageData.hq_state ? `, ${pageData.hq_state}` : ''}
                                    </span>
                                )}
                                {pageData.website && (
                                    <span className="flex align-items-center gap-1 text-xs" style={{ color: 'var(--primary-color)' }}>
                                        <i className="pi pi-globe" style={{ fontSize: '0.75rem' }} />
                                        {pageData.website}
                                    </span>
                                )}
                            </div>

                            {/* Stats bar */}
                            {(() => {
                                const stats = [
                                    { num: fmtNum(pageData.follower_count), lbl: 'Followers',  icon: 'pi-users',     clr: 'var(--blue-500)'   },
                                    { num: pageData.agent_count || 0,       lbl: 'Employees',  icon: 'pi-id-card',   clr: 'var(--green-500)'  },
                                    { num: pageData.post_count || 0,        lbl: 'Posts',      icon: 'pi-file-edit', clr: 'var(--purple-500)' },
                                    ...(pageData.rating_count > 0 ? [{ num: parseFloat(pageData.average_rating || 0).toFixed(1), lbl: 'Rating', icon: 'pi-star-fill', clr: 'var(--yellow-500)' }] : []),
                                ];
                                return (
                                    <div className="flex gap-0 pt-3" style={{ borderTop: '1px solid var(--surface-border)' }}>
                                        {stats.map((s, idx) => (
                                            <div key={s.lbl} className="flex-1 flex flex-column align-items-center py-2"
                                                style={{ borderRight: idx < stats.length - 1 ? '1px solid var(--surface-border)' : 'none' }}>
                                                <i className={`pi ${s.icon} mb-1`} style={{ color: s.clr, fontSize: '1rem' }} />
                                                <span className="font-bold text-900" style={{ fontSize: '1.1rem', lineHeight: 1 }}>{s.num}</span>
                                                <span className="text-color-secondary mt-1" style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.lbl}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* Inline star rating (rate this page) */}
                            <div className="flex align-items-center justify-content-center gap-2 py-2"
                                style={{ borderTop: '1px solid var(--surface-border)' }}>
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-color-secondary)' }}>
                                    {myRating ? 'Your rating:' : 'Rate this page:'}
                                </span>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        disabled={ratingBusy}
                                        onClick={async () => {
                                            if (ratingBusy || !slug) return;
                                            setRatingBusy(true);
                                            const prev = myRating;
                                            setMyRating(star);
                                            try {
                                                const res = await companyPageService.rate(slug, { rating: star });
                                                if (res?.success) {
                                                    setPageData(d => d ? { ...d, average_rating: res.average_rating, rating_count: res.rating_count } : d);
                                                } else {
                                                    setMyRating(prev);
                                                }
                                            } catch {
                                                setMyRating(prev);
                                            } finally {
                                                setRatingBusy(false);
                                            }
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: ratingBusy ? 'wait' : 'pointer',
                                            padding: '0 1px',
                                            fontSize: '1.2rem',
                                            color: star <= (myRating || 0) ? '#f59e0b' : 'var(--surface-300)',
                                            transition: 'color 0.1s',
                                        }}
                                    >★</button>
                                ))}
                                {myRating && (
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-color-secondary)' }}>({myRating}/5)</span>
                                )}
                            </div>
                        </div>

                        {/* ═══ TABS ═══ */}
                        <TabView activeIndex={activeTab} onTabChange={handleTabChange} style={{ borderTop: '1px solid var(--surface-border)' }}>

                            {/* ─── Tab 1: Posts ─── */}
                            <TabPanel header="Posts" leftIcon="pi pi-file-edit mr-2">

                                {/* Quick-compose bar */}
                                {canPost && (
                                    <div className="flex align-items-center gap-3 p-3" style={{ borderBottom: '1px solid var(--surface-border)', background: 'var(--surface-ground)' }}>
                                        <Avatar
                                            label={pageData.name?.slice(0, 2).toUpperCase()}
                                            style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', color: 'var(--primary-color)', fontWeight: '800', flexShrink: 0, fontSize: '0.9rem' }}
                                        />
                                        <div
                                            className="flex-1 cursor-pointer"
                                            style={{ border: '1.5px solid var(--surface-border)', borderRadius: '24px', padding: '10px 18px', background: 'var(--surface-card)', color: 'var(--text-color-secondary)', fontSize: '0.85rem', transition: 'border-color 0.2s' }}
                                            onClick={() => setPostDialog(true)}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--surface-border)'}
                                        >
                                            Share an update on behalf of {pageData.name}…
                                        </div>
                                        <Button
                                            icon="pi pi-image"
                                            className="p-button-rounded p-button-outlined p-button-secondary p-button-sm"
                                            tooltip="Add photo"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => { setPostMediaType('image'); setPostDialog(true); }}
                                        />
                                    </div>
                                )}

                                {/* Posts list */}
                                {postsLoading && posts.length === 0 ? (
                                    <div className="p-4">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="mb-4 p-3 border-round" style={{ border: '1px solid var(--surface-border)' }}>
                                                <div className="flex gap-3 mb-3">
                                                    <Skeleton shape="circle" size="44px" />
                                                    <div className="flex-1">
                                                        <Skeleton height="0.9rem" width="38%" className="mb-2" />
                                                        <Skeleton height="0.75rem" width="55%" />
                                                    </div>
                                                </div>
                                                <Skeleton height="5rem" className="mb-2 border-round" />
                                                <Skeleton height="0.75rem" width="40%" />
                                            </div>
                                        ))}
                                    </div>
                                ) : posts.length === 0 ? (
                                    <div className="flex flex-column align-items-center justify-content-center py-6 px-3 text-center">
                                        <div className="flex align-items-center justify-content-center mb-4 border-round-2xl"
                                            style={{ width: '64px', height: '64px', background: 'var(--surface-100)' }}>
                                            <i className="pi pi-file-edit" style={{ fontSize: '1.75rem', color: 'var(--text-color-secondary)' }} />
                                        </div>
                                        <div className="font-semibold text-900 text-lg mb-2">No posts yet</div>
                                        <div className="text-color-secondary text-sm mb-4" style={{ maxWidth: '260px', lineHeight: 1.6 }}>
                                            {canPost ? `Be the first to share an update on behalf of ${pageData.name}.` : `${pageData.name} hasn't posted anything yet.`}
                                        </div>
                                        {canPost && (
                                            <Button label="Create First Post" icon="pi pi-plus" className="p-button-primary p-button-sm" onClick={() => setPostDialog(true)} />
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="p-3 flex flex-column gap-3">
                                            {posts.map(post => (
                                                <div key={post.id} className="border-round-xl p-3"
                                                    style={{ border: '1px solid var(--surface-border)', background: 'var(--surface-card)', transition: 'box-shadow 0.2s' }}
                                                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                                                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                                >
                                                    {/* Author row */}
                                                    <div className="flex align-items-start justify-content-between gap-3 mb-3">
                                                        <div className="flex align-items-start gap-3">
                                                            {post.is_official_post ? (
                                                                <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: '800', color: 'var(--primary-color)', flexShrink: 0 }}>
                                                                    {pageData.name?.slice(0, 2).toUpperCase()}
                                                                </div>
                                                            ) : (
                                                                <Avatar
                                                                    label={initials(post.author_profile?.full_name)}
                                                                    image={post.author_profile?.profile_photo_url || null}
                                                                    shape="circle"
                                                                    style={{ width: '44px', height: '44px', background: '#F0FDF4', color: '#15803D', fontWeight: '700', flexShrink: 0 }}
                                                                />
                                                            )}
                                                            <div>
                                                                <div className="flex align-items-center gap-2 flex-wrap mb-1">
                                                                    <span className="font-bold text-900" style={{ fontSize: '0.9rem' }}>
                                                                        {post.is_official_post ? pageData.name : post.author_profile?.full_name}
                                                                    </span>
                                                                    {post.is_official_post ? (
                                                                        <span className="flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold"
                                                                            style={{ background: '#F0FDF4', color: '#15803D' }}>
                                                                            <i className="pi pi-building" style={{ fontSize: '0.6rem' }} /> Official
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-1 border-round text-xs font-semibold"
                                                                            style={{ background: '#FFF7ED', color: '#C2410C' }}>
                                                                            {pageData.name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-color-secondary">
                                                                    {post.is_official_post ? 'Company post' : post.author_profile?.current_designation}
                                                                    <span className="mx-1">·</span>
                                                                    {timeAgo(post.created_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <Button icon="pi pi-ellipsis-h" aria-label="Post options" className="p-button-text p-button-secondary p-button-sm flex-shrink-0" style={{ width: '32px', height: '32px' }} onClick={(e) => handlePostMenu(e, post)} />
                                                    </div>

                                                    {/* Content */}
                                                    {post.content && (
                                                        <div className="text-sm text-700 line-height-3 mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                                            {post.content}
                                                        </div>
                                                    )}

                                                    {/* Image */}
                                                    {post.media_type === 'image' && post.media_urls?.[0] && (
                                                        <div className="mb-3 border-round-lg overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                            <img src={post.media_urls[0]} alt="Post" style={{ width: '100%', maxHeight: '340px', objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                                                        </div>
                                                    )}

                                                    {/* Video */}
                                                    {post.media_type === 'video' && post.media_urls?.[0] && (
                                                        <a href={post.media_urls[0]} target="_blank" rel="noopener noreferrer" className="no-underline mb-3 flex align-items-center gap-3 p-3 border-round-lg" style={{ background: 'var(--surface-ground)', border: '1px solid var(--surface-border)' }}>
                                                            <div className="flex align-items-center justify-content-center border-round-lg" style={{ width: '42px', height: '42px', background: '#EFF6FF', flexShrink: 0 }}>
                                                                <i className="pi pi-play-circle text-primary" style={{ fontSize: '1.25rem' }} />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="font-semibold text-primary text-sm">Watch Video</div>
                                                                <div className="text-xs text-color-secondary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.media_urls[0]}</div>
                                                            </div>
                                                        </a>
                                                    )}

                                                    {/* Tags */}
                                                    {post.tags?.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mb-2">
                                                            {post.tags.map(tag => (
                                                                <span key={tag} className="px-2 py-1 border-round text-xs font-medium" style={{ background: '#EFF6FF', color: 'var(--primary-color)' }}>
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Engagement counts */}
                                                    {(post.like_count > 0 || post.comment_count > 0) && (
                                                        <div className="flex align-items-center gap-3 py-2 text-xs text-color-secondary" style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                                            {post.like_count > 0 && (
                                                                <div className="flex align-items-center gap-1">
                                                                    <i className="pi pi-thumbs-up" style={{ color: 'var(--primary-color)' }} />
                                                                    <span>{post.like_count}</span>
                                                                </div>
                                                            )}
                                                            {post.comment_count > 0 && (
                                                                <div className="flex align-items-center gap-1 cursor-pointer" onClick={() => handleCommentToggle(post.id)}>
                                                                    <i className="pi pi-comment" style={{ color: 'var(--text-color-secondary)' }} />
                                                                    <span>{post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Reaction bar */}
                                                    <div className="flex gap-1 pt-2">
                                                        <Button
                                                            icon={post.is_liked ? 'pi pi-thumbs-up-fill' : 'pi pi-thumbs-up'}
                                                            label="Like"
                                                            className={`p-button-text p-button-sm flex-1 justify-content-center ${post.is_liked ? 'p-button-primary' : 'p-button-secondary'}`}
                                                            style={{ fontSize: '0.78rem', borderRadius: '8px' }}
                                                            onClick={() => handleLike(post)}
                                                        />
                                                        <Button
                                                            icon="pi pi-comment"
                                                            label="Comment"
                                                            className={`p-button-text p-button-sm flex-1 justify-content-center ${openCommentPostId === post.id ? 'p-button-primary' : 'p-button-secondary'}`}
                                                            style={{ fontSize: '0.78rem', borderRadius: '8px' }}
                                                            onClick={() => handleCommentToggle(post.id)}
                                                        />
                                                        <Button
                                                            icon={savingId === post.id ? 'pi pi-spin pi-spinner' : post.is_saved ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'}
                                                            label={post.is_saved ? 'Saved' : 'Save'}
                                                            className={`p-button-text p-button-sm flex-1 justify-content-center ${post.is_saved ? 'p-button-primary' : 'p-button-secondary'}`}
                                                            style={{ fontSize: '0.78rem', borderRadius: '8px' }}
                                                            onClick={() => handleSaveToggle(post)}
                                                            disabled={savingId === post.id}
                                                        />
                                                    </div>
                                                    {/* Comments section */}
                                                    {openCommentPostId === post.id && (
                                                        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--surface-border)' }}>
                                                            {/* Existing comments */}
                                                            {commentsLoading === post.id ? (
                                                                <div className="flex flex-column gap-2 mb-2">
                                                                    {[1,2].map(i => <Skeleton key={i} height="2.5rem" borderRadius="8px" />)}
                                                                </div>
                                                            ) : (postComments[post.id] || []).length > 0 ? (
                                                                <div className="flex flex-column gap-2 mb-3">
                                                                    {(postComments[post.id] || []).map((c, i) => {
                                                                        // API returns user_profile.full_name + comment_text
                                                                        // newly added comments use profile.full_name (from addComment)
                                                                        const name = c.user_profile?.full_name || c.profile?.full_name || 'Member';
                                                                        const photo = c.user_profile?.profile_photo_url || c.profile?.profile_photo_url || null;
                                                                        const text  = c.comment_text || c.content || '';
                                                                        return (
                                                                        <div key={c.id || i} className="flex gap-2 align-items-start p-2 border-round-lg" style={{ background: 'var(--surface-ground)' }}>
                                                                            <Avatar
                                                                                label={name.charAt(0).toUpperCase()}
                                                                                image={photo}
                                                                                shape="circle"
                                                                                size="small"
                                                                                style={{ flexShrink: 0, background: 'var(--primary-100)', color: 'var(--primary-color)', fontWeight: '700', fontSize: '0.7rem' }}
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="font-semibold text-xs text-900 mb-1">{name}</div>
                                                                                <div className="text-sm text-color line-height-2">{text}</div>
                                                                            </div>
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : null}
                                                            {/* New comment input */}
                                                            <div className="flex gap-2">
                                                                <InputText
                                                                    value={commentText}
                                                                    onChange={e => setCommentText(e.target.value)}
                                                                    placeholder="Write a comment…"
                                                                    className="flex-1 p-inputtext-sm"
                                                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCommentSubmit(post); } }}
                                                                    autoFocus
                                                                />
                                                                <Button
                                                                    icon={commentSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                                                                    className="p-button-sm p-button-primary"
                                                                    disabled={!commentText.trim() || commentSubmitting}
                                                                    onClick={() => handleCommentSubmit(post)}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {postsPage < postsLastPage && (
                                            <div className="text-center pb-3">
                                                <Button label="Load More" icon="pi pi-chevron-down" className="p-button-outlined p-button-sm p-button-secondary" loading={postsLoading} onClick={() => fetchPosts(postsPage + 1)} />
                                            </div>
                                        )}
                                    </>
                                )}
                            </TabPanel>

                            {/* ─── Tab 2: People ─── */}
                            <TabPanel
                                header="People"
                                leftIcon="pi pi-users mr-2"
                                data-testid="people-tab"
                            >
                                <div className="p-3 pb-2">
                                    <span className="p-input-icon-left w-full">
                                        <i className="pi pi-search" />
                                        <InputText
                                            value={peopleSearch}
                                            onChange={e => setPeopleSearch(e.target.value)}
                                            placeholder="Search by name or role…"
                                            className="w-full"
                                        />
                                    </span>
                                </div>

                                {peopleLoading && people.length === 0 ? (
                                    <div className="p-3 flex flex-column gap-2">
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} className="flex align-items-center gap-3 p-3 border-round" style={{ border: '1px solid var(--surface-border)' }}>
                                                <Skeleton shape="circle" size="48px" />
                                                <div className="flex-1">
                                                    <Skeleton height="0.9rem" width="40%" className="mb-2" />
                                                    <Skeleton height="0.75rem" width="60%" />
                                                </div>
                                                <Skeleton height="2rem" width="90px" borderRadius="8px" />
                                            </div>
                                        ))}
                                    </div>
                                ) : visiblePeople.length === 0 ? (
                                    <div className="flex flex-column align-items-center py-6 text-center px-4">
                                        <i className="pi pi-users text-5xl mb-3" style={{ color: 'var(--surface-300)' }} />
                                        <div className="text-color-secondary text-sm mb-1">No agents have associated with this company yet.</div>
                                        <div className="text-color-secondary text-xs">Agents can appear here by selecting this company under "Companies I Work With" in their profile.</div>
                                    </div>
                                ) : (
                                    <div className="p-3 flex flex-column gap-2">
                                        {visiblePeople
                                            .filter(p =>
                                                !peopleSearch ||
                                                p.full_name?.toLowerCase().includes(peopleSearch.toLowerCase()) ||
                                                p.current_designation?.toLowerCase().includes(peopleSearch.toLowerCase()) ||
                                                p.location_city?.toLowerCase().includes(peopleSearch.toLowerCase())
                                            )
                                            .map(person => {
                                                const ri = roleInfo(person.page_role);
                                                return (
                                                    <div key={person.id} className="flex align-items-center gap-3 p-3 border-round-xl"
                                                        style={{ border: '1px solid var(--surface-border)', background: 'var(--surface-card)', transition: 'background 0.15s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-card)'}
                                                    >
                                                        <Avatar
                                                            label={initials(person.full_name)}
                                                            image={person.profile_photo_url || null}
                                                            shape="circle"
                                                            style={{ width: '48px', height: '48px', flexShrink: 0, background: 'var(--primary-100)', color: 'var(--primary-color)', fontWeight: '700', fontSize: '0.9rem' }}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                                                                <span className="font-semibold text-900" style={{ fontSize: '0.875rem' }}>{person.full_name}</span>
                                                                {person.page_role && person.page_role !== 'member' && (
                                                                    <span style={{ background: ri.bg, color: ri.color, borderRadius: '10px', padding: '1px 8px', fontSize: '0.62rem', fontWeight: '700' }}>
                                                                        {ri.label}
                                                                    </span>
                                                                )}
                                                                {person.is_verified && (
                                                                    <i className="pi pi-verified text-primary" style={{ fontSize: '0.8rem' }} />
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-color-secondary line-height-2">
                                                                {person.current_designation}
                                                                {person.location_city ? <><span className="mx-1">·</span>{person.location_city}</> : ''}
                                                                {person.experience_years ? <><span className="mx-1">·</span>{person.experience_years} yrs</> : ''}
                                                            </div>
                                                        </div>
                                                        {person.is_me ? (
                                                            <Tag value="You" severity="info" style={{ fontSize: '0.7rem', borderRadius: '8px', flexShrink: 0 }} />
                                                        ) : person.connection_status === 'accepted' || sentConnectIds.has(person.id) && false ? (
                                                            /* Already connected — show Connected tag + Message button */
                                                            <div className="flex gap-2 flex-shrink-0">
                                                                <Tag value="Connected" icon="pi pi-check" severity="success" style={{ fontSize: '0.68rem', borderRadius: '8px' }} />
                                                                <Button
                                                                    icon="pi pi-envelope"
                                                                    label="Message"
                                                                    className="p-button-sm p-button-outlined"
                                                                    style={{ fontSize: '0.75rem', borderRadius: '8px' }}
                                                                    onClick={() => navigate(`/app/messaging?profileId=${person.id}`)}
                                                                />
                                                            </div>
                                                        ) : person.connection_status === 'pending' ? (
                                                            /* Request already sent/received */
                                                            <Tag value="Pending" icon="pi pi-clock" severity="warning" style={{ fontSize: '0.68rem', borderRadius: '8px', flexShrink: 0 }} />
                                                        ) : sentConnectIds.has(person.id) ? (
                                                            /* Just sent in this session */
                                                            <Tag value="Sent" icon="pi pi-check" severity="success" style={{ fontSize: '0.68rem', borderRadius: '8px', flexShrink: 0 }} />
                                                        ) : (
                                                            /* Not yet connected */
                                                            <Button
                                                                icon={connectingId === person.id ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
                                                                label="Connect"
                                                                className="p-button-sm p-button-outlined flex-shrink-0"
                                                                style={{ fontSize: '0.75rem', borderRadius: '8px' }}
                                                                onClick={() => handleConnect(person)}
                                                                disabled={!!connectingId}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                )}
                            </TabPanel>

                            {/* ─── Tab 3: About ─── */}
                            <TabPanel header="About" leftIcon="pi pi-info-circle mr-2" data-testid="about-tab">
                                <div className="p-4">
                                    <div className="flex flex-column gap-3">
                                        {pageData.description && (
                                            <div>
                                                <div className="font-bold text-900 mb-2">About</div>
                                                <p className="text-color-secondary line-height-3 m-0">{pageData.description}</p>
                                            </div>
                                        )}
                                        {pageData.industry && (
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-briefcase text-primary" />
                                                <span className="text-900">{pageData.industry}</span>
                                            </div>
                                        )}
                                        {pageData.website && (
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-globe text-primary" />
                                                <a href={pageData.website} target="_blank" rel="noopener noreferrer" className="text-primary no-underline hover:underline">{pageData.website}</a>
                                            </div>
                                        )}
                                        {pageData.contact_phone && (
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-phone text-primary" />
                                                <span className="text-900">{pageData.contact_phone}</span>
                                            </div>
                                        )}
                                        {pageData.contact_email && (
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-envelope text-primary" />
                                                <span className="text-900">{pageData.contact_email}</span>
                                            </div>
                                        )}
                                        {!pageData.description && !pageData.industry && !pageData.website && !pageData.contact_phone && !pageData.contact_email && (
                                            <div className="text-center py-4 text-color-secondary">
                                                <i className="pi pi-info-circle text-4xl mb-2 block" />
                                                <p className="m-0">No company information available.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>

                            {/* ─── Tab 4: Page Admin ─── */}
                            {myRole && (
                                <TabPanel header="Page Admin" leftIcon="pi pi-shield mr-2">
                                    <div className="p-4">

                                        {/* Info banner */}
                                        <div className="flex gap-3 p-3 border-round-lg mb-4"
                                            style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFFBEB)', border: '1px solid #FED7AA' }}>
                                            <div className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                                                style={{ width: '40px', height: '40px', background: '#FEF3C7' }}>
                                                <i className="pi pi-shield" style={{ color: '#D97706', fontSize: '1rem' }} />
                                            </div>
                                            <div>
                                                <div className="font-semibold mb-1" style={{ color: '#92400E', fontSize: '0.875rem' }}>Page Admin Panel</div>
                                                <div className="text-xs line-height-3" style={{ color: '#78350F' }}>
                                                    Control who can post and manage this company page. Only Super Admins can add or remove editors.
                                                </div>
                                            </div>
                                        </div>

                                        {/* Managers header */}
                                        <div className="flex align-items-center justify-content-between mb-3">
                                            <div>
                                                <div className="font-bold text-900 mb-1">Page Editors</div>
                                                <div className="text-xs text-color-secondary">People who can post on behalf of {pageData.name}</div>
                                            </div>
                                            {isSuperAdmin && (
                                                <Button label="Give Access" icon="pi pi-user-plus" className="p-button-sm p-button-primary" onClick={openGiveAccessDialog} />
                                            )}
                                        </div>

                                        {/* Manager cards */}
                                        {managersLoading ? (
                                            <div className="flex flex-column gap-2 mb-4">
                                                {[1, 2].map(i => (
                                                    <div key={i} className="flex align-items-center gap-3 p-3 border-round" style={{ border: '1px solid var(--surface-border)' }}>
                                                        <Skeleton shape="circle" size="44px" />
                                                        <div className="flex-1"><Skeleton height="0.875rem" width="35%" className="mb-2" /><Skeleton height="0.75rem" width="25%" /></div>
                                                        <Skeleton height="2rem" width="130px" borderRadius="8px" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : managers.length === 0 ? (
                                            <div className="text-center py-4 text-color-secondary text-sm mb-4">
                                                No editors yet. Click "Give Access" to add one.
                                            </div>
                                        ) : (
                                            <div className="flex flex-column gap-2 mb-4">
                                                {managers.map(m => {
                                                    const ri = roleInfo(m.role);
                                                    return (
                                                        <div key={m.id} className="flex align-items-center gap-3 p-3 border-round-xl"
                                                            style={{ border: '1px solid var(--surface-border)', background: 'var(--surface-card)' }}>
                                                            <Avatar
                                                                label={initials(m.profile?.full_name)}
                                                                image={m.profile?.profile_photo_url || null}
                                                                shape="circle"
                                                                style={{ width: '44px', height: '44px', flexShrink: 0, background: ri.bg, color: ri.color, fontWeight: '700' }}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-semibold text-900 text-sm">{m.profile?.full_name}</div>
                                                                <div className="text-xs text-color-secondary">{m.profile?.current_designation || m.profile?.email}</div>
                                                            </div>
                                                            {isSuperAdmin ? (
                                                                <Dropdown
                                                                    value={m.role}
                                                                    options={ROLES}
                                                                    onChange={e => handleRoleChange(m.id, e.value)}
                                                                    className="p-inputtext-sm flex-shrink-0"
                                                                    style={{ minWidth: '140px' }}
                                                                />
                                                            ) : (
                                                                <span style={{ background: ri.bg, color: ri.color, borderRadius: '12px', padding: '3px 12px', fontSize: '0.7rem', fontWeight: '700' }}>
                                                                    {ri.label}
                                                                </span>
                                                            )}
                                                            {/* Super admin cannot remove themselves */}
                                                            {isSuperAdmin && m.role !== 'super_admin' && (
                                                                <Button
                                                                    icon={removingId === m.id ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
                                                                    aria-label="Remove access"
                                                                    className="p-button-sm p-button-text p-button-danger flex-shrink-0"
                                                                    onClick={() => handleRemoveManager(m.id, m.profile?.full_name)}
                                                                    disabled={!!removingId}
                                                                    tooltip="Remove access"
                                                                    tooltipOptions={{ position: 'top' }}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Permissions table */}
                                        <div>
                                            <div className="font-bold text-900 text-sm mb-3">Role Permissions</div>
                                            <div className="border-round-xl overflow-hidden" style={{ border: '1px solid var(--surface-border)' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                    <thead>
                                                        <tr style={{ background: 'var(--surface-ground)' }}>
                                                            {['Permission', 'Super Admin', 'Editor'].map((h, i) => (
                                                                <th key={h} style={{ padding: '10px 14px', textAlign: i === 0 ? 'left' : 'center', borderBottom: '1px solid var(--surface-border)', fontSize: '0.7rem', fontWeight: '700', color: 'var(--text-color-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                                    {h}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[
                                                            ['View company feed',          true,  true ],
                                                            ['Post on behalf of company',  true,  true ],
                                                            ['Upload images & video',      true,  true ],
                                                            ['Delete any post',            true,  false],
                                                            ['Add / remove editors',       true,  false],
                                                        ].map(([label, sa, ed], rowIdx) => (
                                                            <tr key={label} style={{ background: rowIdx % 2 === 1 ? 'var(--surface-ground)' : 'var(--surface-card)' }}>
                                                                <td style={{ padding: '10px 14px', color: 'var(--text-color)', fontWeight: '500' }}>{label}</td>
                                                                {[sa, ed].map((v, i) => (
                                                                    <td key={i} style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                                        <i className={`pi ${v ? 'pi-check-circle' : 'pi-times-circle'}`}
                                                                            style={{ fontSize: '1rem', color: v ? '#22C55E' : 'var(--surface-300)' }} />
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                            )}
                        </TabView>
                    </div>
                </div>

                {/* ═══════════════ RIGHT SIDEBAR ═══════════════ */}
                <div className="col-12 lg:col-4">

                    {/* Stats cards (admin/editor) */}
                    {canPost && (
                        <div className="card p-3 mb-3">
                            <div className="flex align-items-center gap-2 mb-3">
                                <i className="pi pi-chart-line text-primary" />
                                <span className="font-bold text-900">Page Analytics</span>
                            </div>
                            <div className="grid m-0 gap-2">
                                {[
                                    { label: 'Posts',       value: pageData.post_count || 0,        icon: 'pi-file-edit', bg: '#EFF6FF', clr: '#3B82F6' },
                                    { label: 'Employees',   value: pageData.agent_count || 0,       icon: 'pi-users',     bg: '#F0FDF4', clr: '#22C55E' },
                                    { label: 'Followers',   value: fmtNum(pageData.follower_count), icon: 'pi-heart',     bg: '#FFF7ED', clr: '#F97316' },
                                ].map(s => (
                                    <div key={s.label} className="col flex align-items-center gap-2 p-3 border-round-lg" style={{ border: '1px solid var(--surface-border)', minWidth: 0 }}>
                                        <div className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                            style={{ width: '36px', height: '36px', background: s.bg }}>
                                            <i className={`pi ${s.icon}`} style={{ color: s.clr, fontSize: '0.9rem' }} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-900" style={{ fontSize: '1.1rem', lineHeight: 1 }}>{s.value}</div>
                                            <div className="text-color-secondary" style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '2px' }}>{s.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* About card */}
                    <div className="card p-3 mb-3">
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-building text-primary" />
                            <span className="font-bold text-900">About {pageData.name}</span>
                        </div>
                        <div className="flex flex-column gap-2">
                            {[
                                { icon: 'pi-briefcase', clr: '#3B82F6', label: 'Industry',    value: pageData.industry },
                                { icon: 'pi-map-marker', clr: '#10B981', label: 'HQ',          value: pageData.hq_city },
                                { icon: 'pi-globe',      clr: '#6366F1', label: 'Website',     value: pageData.website },
                                { icon: 'pi-envelope',   clr: '#F59E0B', label: 'Email',       value: pageData.contact_email },
                                { icon: 'pi-phone',      clr: '#8B5CF6', label: 'Phone',       value: pageData.contact_phone },
                            ].filter(r => r.value).map(r => (
                                <div key={r.label} className="flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                    <i className={`pi ${r.icon} flex-shrink-0`} style={{ color: r.clr, fontSize: '0.85rem', width: '16px' }} />
                                    <span className="text-color-secondary text-xs flex-shrink-0" style={{ minWidth: '60px' }}>{r.label}</span>
                                    <span className="font-medium text-900 text-xs text-right flex-1" style={{ wordBreak: 'break-all' }}>{r.value}</span>
                                </div>
                            ))}
                            <div className="flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                <i className="pi pi-id-card flex-shrink-0" style={{ color: '#22C55E', fontSize: '0.85rem', width: '16px' }} />
                                <span className="text-color-secondary text-xs flex-shrink-0" style={{ minWidth: '60px' }}>Members</span>
                                <span className="font-bold text-primary text-xs text-right flex-1">{pageData.agent_count || 0} on Lending Network</span>
                            </div>
                        </div>
                    </div>

                    {/* Internal notice */}
                    <div className="card p-3 border-round-xl" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)', border: '1px solid #C7D2FE' }}>
                        <div className="flex gap-3 align-items-start">
                            <div className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                                style={{ width: '36px', height: '36px', background: 'rgba(99,102,241,0.12)' }}>
                                <i className="pi pi-lock" style={{ color: '#4F46E5', fontSize: '0.9rem' }} />
                            </div>
                            <div>
                                <div className="font-bold mb-1" style={{ color: '#312E81', fontSize: '0.875rem' }}>Internal Company Feed</div>
                                <div className="text-xs line-height-3" style={{ color: '#4338CA' }}>
                                    This feed is only visible to <strong>{pageData.name}</strong> members on the Lending Network. Posts here are not visible to other companies or the public feed.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════════ POST CREATION DIALOG ═══════════════ */}
            <Dialog
                visible={postDialog}
                onHide={() => { setPostDialog(false); resetPostForm(); }}
                header={
                    <div className="flex align-items-center gap-3">
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', color: 'var(--primary-color)', flexShrink: 0 }}>
                            {pageData.name?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                            <div className="font-bold text-900">{pageData.name}</div>
                            <div className="flex align-items-center gap-1 mt-1" style={{ fontSize: '0.72rem', color: 'var(--text-color-secondary)' }}>
                                <i className="pi pi-lock" style={{ fontSize: '0.6rem' }} />
                                <span>Internal · Only visible to {pageData.name} members</span>
                            </div>
                        </div>
                    </div>
                }
                style={{ width: '560px' }}
                modal
                footer={
                    <div className="flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid var(--surface-border)' }}>
                        <div className="flex gap-1">
                            <Button
                                icon="pi pi-image"
                                className={`p-button-rounded p-button-sm ${postMediaType === 'image' ? 'p-button-primary' : 'p-button-text p-button-secondary'}`}
                                tooltip="Add photo"
                                tooltipOptions={{ position: 'top' }}
                                disabled={uploadingMedia}
                                onClick={() => {
                                    if (postMediaType === 'image') { setPostMediaType(null); setUploadedImageUrls([]); fileUploadRef.current?.clear(); }
                                    else { setPostMediaType('image'); setPostVideoUrl(''); }
                                }}
                            />
                            <Button
                                icon="pi pi-video"
                                className={`p-button-rounded p-button-sm ${postMediaType === 'video' ? 'p-button-primary' : 'p-button-text p-button-secondary'}`}
                                tooltip="Add video link"
                                tooltipOptions={{ position: 'top' }}
                                disabled={uploadingMedia}
                                onClick={() => {
                                    if (postMediaType === 'video') { setPostMediaType(null); setPostVideoUrl(''); }
                                    else { setPostMediaType('video'); setUploadedImageUrls([]); fileUploadRef.current?.clear(); }
                                }}
                            />
                            <Button
                                icon="pi pi-tag"
                                className="p-button-rounded p-button-sm p-button-text p-button-secondary"
                                tooltip="Add tags"
                                tooltipOptions={{ position: 'top' }}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button label="Cancel" className="p-button-text p-button-secondary p-button-sm" onClick={() => { setPostDialog(false); resetPostForm(); }} />
                            <Button
                                label="Post"
                                icon="pi pi-send"
                                className="p-button-primary p-button-sm"
                                loading={postSubmitting}
                                disabled={uploadingMedia || postSubmitting}
                                onClick={handlePostSubmit}
                            />
                        </div>
                    </div>
                }
            >
                {/* Post type pills */}
                <div className="flex gap-2 mb-3">
                    {POST_TYPES.map(t => (
                        <Button
                            key={t.value}
                            label={t.label}
                            className={`p-button-sm p-button-rounded ${postType === t.value ? 'p-button-primary' : 'p-button-outlined p-button-secondary'}`}
                            style={{ fontSize: '0.72rem' }}
                            onClick={() => setPostType(t.value)}
                        />
                    ))}
                </div>

                {/* Textarea */}
                <InputTextarea
                    value={postContent}
                    onChange={e => setPostContent(e.target.value)}
                    placeholder={`What would you like to share on behalf of ${pageData.name}?`}
                    rows={5}
                    autoResize
                    className="w-full mb-3"
                    style={{ fontSize: '0.9rem', lineHeight: '1.65', resize: 'none', border: '1.5px solid var(--surface-border)', borderRadius: '10px' }}
                />

                {/* Image upload */}
                {postMediaType === 'image' && (
                    <div className="mb-3 p-3 border-round-lg" style={{ background: 'var(--surface-ground)', border: '1.5px dashed var(--surface-border)' }}>
                        {uploadedImageUrls.length === 0 ? (
                            <>
                                <FileUpload
                                    ref={fileUploadRef}
                                    name="files[]"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    maxFileSize={5242880}
                                    multiple customUpload auto
                                    uploadHandler={handleFileUpload}
                                    chooseLabel={uploadingMedia ? 'Uploading…' : 'Choose Images'}
                                    disabled={uploadingMedia}
                                    mode="basic"
                                    className="w-full"
                                />
                                {uploadingMedia && (
                                    <div className="flex align-items-center gap-2 mt-2 text-xs text-color-secondary">
                                        <i className="pi pi-spin pi-spinner text-primary" /> Uploading to cloud…
                                    </div>
                                )}
                                <div className="text-xs text-color-secondary mt-2">JPG, PNG, GIF, WebP · Max 5 MB each</div>
                            </>
                        ) : (
                            <div>
                                <div className="grid">
                                    {uploadedImageUrls.map((url, idx) => (
                                        <div key={idx} className="col-6 relative p-1">
                                            <img src={url} alt={`Upload ${idx + 1}`} className="border-round-lg w-full" style={{ height: '120px', objectFit: 'cover' }} />
                                            <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-sm" style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', padding: 0 }}
                                                onClick={() => {
                                                    const rem = uploadedImageUrls.filter((_, i) => i !== idx);
                                                    setUploadedImageUrls(rem);
                                                    if (rem.length === 0) { setPostMediaType(null); fileUploadRef.current?.clear(); }
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <Button label="Remove all" icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm mt-2" style={{ fontSize: '0.72rem' }} onClick={() => { setUploadedImageUrls([]); fileUploadRef.current?.clear(); }} />
                            </div>
                        )}
                    </div>
                )}

                {/* Video link */}
                {postMediaType === 'video' && (
                    <div className="mb-3">
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon"><i className="pi pi-link" /></span>
                            <InputText value={postVideoUrl} onChange={e => setPostVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full" />
                        </div>
                    </div>
                )}

                {/* Tags */}
                <div>
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon"><i className="pi pi-hashtag" /></span>
                        <InputText value={postTags} onChange={e => setPostTags(e.target.value)} placeholder="HomeLoan, AxisBank, LendingNetwork" className="w-full" />
                    </div>
                    <div className="text-xs text-color-secondary mt-1">Separate tags with commas</div>
                </div>
            </Dialog>

            {/* ═══════════════ GIVE ACCESS DIALOG ═══════════════ */}
            <Dialog
                visible={giveAccessDialog}
                onHide={() => setGiveAccessDialog(false)}
                header={
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                            style={{ width: '40px', height: '40px', background: '#EFF6FF' }}>
                            <i className="pi pi-shield text-primary" style={{ fontSize: '1.1rem' }} />
                        </div>
                        <div>
                            <div className="font-bold text-900">Give Page Access</div>
                            <div className="text-xs text-color-secondary mt-1">Select an employee and assign their role on this page.</div>
                        </div>
                    </div>
                }
                style={{ width: '520px' }}
                modal
                footer={
                    <Button label="Close" icon="pi pi-times" className="p-button-outlined p-button-secondary p-button-sm" onClick={() => setGiveAccessDialog(false)} />
                }
            >
                {/* Role pills */}
                <div className="flex gap-2 mb-3">
                    {ROLES.map(r => (
                        <span key={r.value} className="flex align-items-center gap-1 px-3 py-1 border-round-3xl text-xs font-bold"
                            style={{ background: r.bg, color: r.color, border: `1px solid ${r.color}40` }}>
                            {r.label}
                        </span>
                    ))}
                </div>

                {/* Search */}
                <span className="p-input-icon-left w-full mb-3">
                    <i className="pi pi-search" />
                    <InputText value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} placeholder="Search by name or designation…" className="w-full" />
                </span>

                {employeesLoading ? (
                    <div className="flex flex-column gap-2">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex align-items-center gap-3 p-3 border-round" style={{ border: '1px solid var(--surface-border)' }}>
                                <Skeleton shape="circle" size="44px" />
                                <div className="flex-1"><Skeleton height="0.875rem" width="45%" className="mb-2" /><Skeleton height="0.75rem" width="30%" /></div>
                                <Skeleton height="2.2rem" width="7rem" borderRadius="8px" />
                            </div>
                        ))}
                    </div>
                ) : employees.length === 0 ? (
                    <div className="flex flex-column align-items-center py-5">
                        <div className="flex align-items-center justify-content-center border-round-2xl mb-3" style={{ width: '56px', height: '56px', background: '#F0FDF4' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '1.5rem', color: '#22C55E' }} />
                        </div>
                        <div className="font-semibold text-900 text-sm">All employees have page access.</div>
                    </div>
                ) : (
                    <div className="flex flex-column gap-2" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                        {employees
                            .filter(e => !employeeSearch || e.full_name?.toLowerCase().includes(employeeSearch.toLowerCase()) || e.current_designation?.toLowerCase().includes(employeeSearch.toLowerCase()))
                            .map(emp => (
                                <div key={emp.id} className="p-3 border-round-xl" style={{ border: '1px solid var(--surface-border)', background: 'var(--surface-card)' }}>
                                    <div className="flex align-items-center gap-3 mb-3">
                                        <Avatar
                                            label={initials(emp.full_name)}
                                            image={emp.profile_photo_url || null}
                                            shape="circle"
                                            style={{ width: '44px', height: '44px', flexShrink: 0, background: 'var(--primary-100)', color: 'var(--primary-color)', fontWeight: '700' }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-900 text-sm">{emp.full_name}</div>
                                            <div className="text-xs text-color-secondary">
                                                {emp.current_designation}{emp.location_city ? ` · ${emp.location_city}` : ''}
                                            </div>
                                        </div>
                                        {emp.is_verified && <i className="pi pi-verified text-primary flex-shrink-0" />}
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <span className="text-xs text-color-secondary font-semibold flex-shrink-0">Role:</span>
                                        <Dropdown
                                            value={employeeRoles[emp.id] || 'editor'}
                                            options={ROLES}
                                            onChange={e => setEmployeeRoles(prev => ({ ...prev, [emp.id]: e.value }))}
                                            className="p-inputtext-sm flex-1"
                                            disabled={!!grantingId}
                                        />
                                        <Button
                                            label={grantingId === emp.id ? 'Granting…' : 'Give Access'}
                                            icon={grantingId === emp.id ? 'pi pi-spin pi-spinner' : 'pi pi-user-check'}
                                            className="p-button-sm p-button-primary flex-shrink-0"
                                            onClick={() => handleGrantAccess(emp)}
                                            disabled={!!grantingId}
                                        />
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default CompanyInternalFeed;
